import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLanguage } from '@/i18n/language-hooks';
import { COUNTRY_OPTIONS, countryLabel } from '@/lib/countries';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

interface CountrySelectProps {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  placeholder?: string;
  className?: string;
}

export function CountrySelect({ value, onChange, id, placeholder, className }: CountrySelectProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const searchPlaceholder = placeholder ?? t('common.countrySelect.searchPlaceholder');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          {value ? countryLabel(value) : (placeholder ?? 'Select country')}
          <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_OPTIONS.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => {
                    onChange(country.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === country.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {country.name} ({country.code})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface CountryMultiSelectProps {
  value: string[];
  onChange: (codes: string[]) => void;
  id?: string;
  placeholder?: string;
}

export function CountryMultiSelect({ value, onChange, id, placeholder }: CountryMultiSelectProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const searchPlaceholder = placeholder ?? t('common.countrySelect.searchPlaceholder');

  const toggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const label =
    value.length === 0
      ? (placeholder ?? 'Select countries')
      : value.map((code) => countryLabel(code)).join(', ');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='h-auto min-h-10 w-full justify-between py-2 font-normal'
        >
          <span className='line-clamp-2 text-left'>{label}</span>
          <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_OPTIONS.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => toggle(country.code)}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value.includes(country.code) ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {country.name} ({country.code})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
